import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faRocket,
    faUsers,
    faLightbulb,
    faHeart,
    faArrowRight,
    faCalendarAlt,
    faEnvelope
} from '@fortawesome/free-solid-svg-icons';
import raul from "../../assets/raul.png";
import luis from "../../assets/luis.png";
import koda from "../../assets/koda.png";

const AboutPage = () => {
    const values = [
        {
            icon: faRocket,
            title: "Innovación",
            description: "Buscamos constantemente nuevas formas de mejorar la fidelización de clientes"
        },
        {
            icon: faUsers,
            title: "Enfoque en Pymes",
            description: "Diseñamos soluciones específicamente para las necesidades de pequeñas y medianas empresas"
        },
        {
            icon: faLightbulb,
            title: "Simplicidad",
            description: "Creemos que la tecnología debe ser accesible y fácil de usar"
        },
        {
            icon: faHeart,
            title: "Compromiso",
            description: "Nos comprometemos con el éxito de nuestros clientes"
        }
    ];

    return (
        <div className="min-h-screen bg-white text-gray-800">
            <Helmet>
                <title>
                    Acerca de PetitPrime – Empresa de Fidelización y Equipo de Software de Lealtad
                </title>
                <meta
                    name="description"
                    content="Conoce la historia, misión y el equipo detrás de PetitPrime, el ecosistema de fidelización digital que transforma la interacción con tus clientes. Innovación, compromiso y excelencia en cada solución."
                />
                <meta
                    name="keywords"
                    content="empresa de fidelización, equipo de software de lealtad, PetitPrime, fidelización digital"
                />
            </Helmet>

            {/* Hero Section */}
            <header className="bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 text-white py-20">
                <div className="container mx-auto px-4 text-center">
                    <h1 className="text-5xl lg:text-6xl font-bold mb-6">
                        Acerca de
                        <span className="text-yellow-400 block">PetitPrime</span>
                    </h1>
                    <p className="text-xl mb-8 text-blue-100 max-w-3xl mx-auto">
                        Somos un equipo apasionado por la tecnología y la fidelización de clientes,
                        comprometidos en ayudar a las pymes a crecer y prosperar.
                    </p>
                </div>
            </header>

            <main className="container mx-auto px-4 py-16">
                {/* Historia Section */}
                <section className="mb-20">
                    <div className="max-w-4xl mx-auto text-center">
                        <h2 className="text-4xl font-bold mb-8 text-gray-800">Nuestra Historia</h2>
                        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 lg:p-12">
                            <p className="text-lg text-gray-700 leading-relaxed mb-6">
                                PetitPrime es un ecosistema de fidelización digital de clientes que nace en 2020 con el objetivo
                                de ayudar a las empresas a fidelizar a sus clientes y aumentar sus ventas. Durante el inicio del
                                covid en 2021, surgió la necesidad de apoyar a pequeños y medianos comercios a recuperar su lugar
                                en el mercado, lo que nos impulsó a desarrollar una solución omnicanal, personalizada y automatizada.
                            </p>
                            <p className="text-lg text-gray-700 leading-relaxed">
                                Hoy, seguimos comprometidos con nuestra misión original: hacer que la fidelización de clientes
                                sea accesible, efectiva y simple para todas las pymes.
                            </p>
                        </div>
                    </div>
                </section>

                {/* Misión y Visión Section */}
                <section className="mb-20">
                    <div className="grid lg:grid-cols-2 gap-12">
                        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-8">
                            <h3 className="text-2xl font-bold mb-4 text-gray-800">Nuestra Misión</h3>
                            <p className="text-gray-700 leading-relaxed">
                                Ofrecer una solución integral de fidelización que permita a las empresas crear programas de lealtad
                                efectivos, impulsando la interacción y la fidelidad del cliente. Nos comprometemos con la innovación
                                y la excelencia en el servicio, siempre buscando superar las expectativas de nuestros clientes.
                            </p>
                        </div>
                        <div className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-2xl p-8">
                            <h3 className="text-2xl font-bold mb-4 text-gray-800">Nuestra Visión</h3>
                            <p className="text-gray-700 leading-relaxed">
                                Ser la plataforma de referencia en fidelización digital para pymes, democratizando el acceso a
                                herramientas avanzadas de lealtad de clientes y contribuyendo al crecimiento sostenible del
                                comercio local.
                            </p>
                        </div>
                    </div>
                </section>

                {/* Valores Section */}
                <section className="mb-20">
                    <div className="text-center mb-12">
                        <h2 className="text-4xl font-bold mb-4 text-gray-800">Nuestros Valores</h2>
                        <p className="text-xl text-gray-600">
                            Los principios que guían nuestro trabajo y relación con los clientes
                        </p>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {values.map((value, index) => (
                            <div key={index} className="text-center p-6 bg-white rounded-2xl shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300">
                                <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                    <FontAwesomeIcon icon={value.icon} className="text-2xl text-white" />
                                </div>
                                <h3 className="text-xl font-bold text-gray-800 mb-3">{value.title}</h3>
                                <p className="text-gray-600">{value.description}</p>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Equipo Section */}
                <section className="mb-20">
                    <div className="text-center mb-12">
                        <h2 className="text-4xl font-bold mb-4 text-gray-800">Nuestro Equipo</h2>
                        <p className="text-xl text-gray-600">
                            Conoce a las personas detrás de PetitPrime
                        </p>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100 hover:shadow-xl transition-all duration-300">
                            <div className="p-8 text-center">
                                <img
                                    src={luis}
                                    alt="Luis Meza"
                                    className="w-32 h-32 object-cover rounded-full mx-auto mb-6 border-4 border-blue-100"
                                />
                                <h3 className="text-2xl font-bold text-gray-800 mb-2">LUIS MEZA</h3>
                                <p className="text-blue-600 font-semibold mb-4">Co-Fundador & CEO</p>
                                <p className="text-gray-600 leading-relaxed">
                                    Master en Gestión de Empresas y especialista en Inteligencia Artificial.
                                    Ha implementado soluciones de IA para grandes compañías como Movistar,
                                    Universidad del Pacífico, Goldman Sachs y USAID.
                                </p>
                            </div>
                        </div>
                        <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100 hover:shadow-xl transition-all duration-300">
                            <div className="p-8 text-center">
                                <img
                                    src={raul}
                                    alt="Raül de Arriba"
                                    className="w-32 h-32 object-cover rounded-full mx-auto mb-6 border-4 border-blue-100"
                                />
                                <h3 className="text-2xl font-bold text-gray-800 mb-2">RAÜL DE ARRIBA</h3>
                                <p className="text-blue-600 font-semibold mb-4">Co-Fundador & CTO</p>
                                <p className="text-gray-600 leading-relaxed">
                                    Ingeniero informático con más de 10 años de experiencia en desarrollo web,
                                    móvil, asistencia técnica y ciberseguridad. Ha colaborado con Logicalis
                                    Spain y la Universidad Autónoma de Barcelona.
                                </p>
                            </div>
                        </div>
                        <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100 hover:shadow-xl transition-all duration-300">
                            <div className="p-8 text-center">
                                <img
                                    src={koda}
                                    alt="Koda"
                                    className="w-32 h-32 object-cover rounded-full mx-auto mb-6 border-4 border-blue-100"
                                />
                                <h3 className="text-2xl font-bold text-gray-800 mb-2">KODA</h3>
                                <p className="text-blue-600 font-semibold mb-4">Chief Happiness Officer</p>
                                <p className="text-gray-600 leading-relaxed">
                                    El inseparable compañero de Raül, un border collie que aporta un toque
                                    humano y cercano en cada reunión. Especialista en mantener el buen
                                    ambiente en el equipo.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>
            </main>

            {/* CTA Section */}
            <section className="py-20 bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
                <div className="container mx-auto px-4 text-center">
                    <h2 className="text-4xl font-bold mb-6">
                        ¿Quieres Conocer Más Sobre PetitPrime?
                    </h2>
                    <p className="text-xl mb-8 text-blue-100 max-w-2xl mx-auto">
                        Únete a nosotros y sé parte de la transformación en fidelización digital
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <Link
                            to="/register"
                            className="bg-yellow-400 hover:bg-yellow-500 text-blue-900 font-bold py-4 px-8 rounded-full text-lg shadow-lg transform hover:scale-105 transition-all duration-300 flex items-center justify-center"
                        >
                            <FontAwesomeIcon icon={faRocket} className="mr-2" />
                            Prueba Gratis 30 Días
                            <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                        </Link>
                        <Link
                            to="https://calendly.com/d/cpp2-rhr-c8q/reunion-de-30-minutos-petitprime"
                            className="border-2 border-white hover:bg-white hover:text-blue-600 text-white font-bold py-4 px-8 rounded-full text-lg transition-all duration-300 flex items-center justify-center"
                        >
                            <FontAwesomeIcon icon={faCalendarAlt} className="mr-2" />
                            Agenda una Reunión
                        </Link>
                    </div>
                    <div className="mt-8 text-blue-200">
                        <p className="mb-2">¿Tienes preguntas? Contáctanos:</p>
                        <a
                            href="mailto:info@petitstores.com"
                            className="inline-flex items-center text-blue-200 hover:text-white transition-colors duration-300"
                        >
                            <FontAwesomeIcon icon={faEnvelope} className="mr-2" />
                            info@petitstores.com
                        </a>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default AboutPage;
